import { Component } from '@theme/component';

/** @extends {Component<{ viewport: HTMLElement[], track: HTMLElement[] }>} */
class MindxbridgeTestimonials extends Component {
  #controller;
  #resize;
  #intersection;
  #frame = 0;
  #lastTime = 0;
  #rows = [];
  #motion;
  #manualPause = false;
  #hovered = false;
  #focused = false;
  #visible = false;
  #drag;
  #suppressClick = false;
  #clickResetTimer = 0;
  #desktop = matchMedia('(min-width: 701px)');
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  #initialize() {
    this.#cleanup();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#rows = [...this.querySelectorAll('.mxb-testimonials__viewport')].map((viewport) => {
      const track = viewport.querySelector('.mxb-testimonials__track');
      const cards = track ? [...track.children] : [];
      return { viewport, track, cards, originalCards: [...cards], offset: 0, step: 0, width: 0 };
    }).filter((row) => row.track && row.cards.length);
    this.#motion = this.querySelector('[data-action="motion"]');
    if (!this.#rows.length) return;
    this.classList.add('is-enhanced');
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('toggle', this.#handleToggle, { capture: true, signal });
    this.addEventListener('focusin', this.#focusIn, { signal });
    this.addEventListener('focusout', this.#focusOut, { signal });
    this.addEventListener('keydown', this.#keyDown, { signal });
    for (const row of this.#rows) {
      row.viewport.addEventListener('pointerdown', this.#pointerDown, { signal });
      row.viewport.addEventListener('pointermove', this.#pointerMove, { signal });
      row.viewport.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.#hovered = true;
        this.#sync();
      }, { signal });
      row.viewport.addEventListener('pointerleave', () => { this.#hovered = false; this.#sync(); }, { signal });
    }
    document.addEventListener('pointerup', this.#pointerEnd, { signal });
    document.addEventListener('pointercancel', this.#pointerEnd, { signal });
    this.#reduced.addEventListener('change', this.#measure, { signal });
    this.#desktop.addEventListener('change', this.#measure, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });
    document.addEventListener('shopify:block:select', this.#blockSelect, { signal });
    document.addEventListener('shopify:block:deselect', () => { this.#manualPause = false; this.#sync(); }, { signal });
    this.#resize = new ResizeObserver(this.#measure);
    for (const row of this.#rows) this.#resize.observe(row.viewport);
    this.#intersection = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    }, { rootMargin: '80px' });
    this.#intersection.observe(this);
    this.#measure();
  }

  #cleanup() {
    this.#controller?.abort();
    this.#resize?.disconnect();
    this.#intersection?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#clickResetTimer);
    this.#frame = 0;
    this.#lastTime = 0;
    this.#drag = undefined;
    this.#suppressClick = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #static(row) {
    return this.#desktop.matches && row.viewport.classList.contains('mxb-testimonials__viewport--written');
  }

  #measure = () => {
    for (const row of this.#rows) {
      const oldStep = row.step;
      if (this.#static(row)) {
        if (row.cards.some((card, index) => card !== row.originalCards[index])) {
          row.track.append(...row.originalCards);
          row.cards = [...row.originalCards];
        }
        row.offset = 0;
        row.viewport.scrollLeft = 0;
        row.track.style.removeProperty('transform');
      }
      row.width = row.viewport.clientWidth;
      if (!row.viewport.querySelector('details[open]')) {
        row.viewport.style.removeProperty('--mxb-card-height');
        const height = Math.max(...row.cards.map((card) => card.getBoundingClientRect().height));
        row.viewport.style.setProperty('--mxb-card-height', Math.ceil(height) + 'px');
      }
      row.step = this.#step(row);
      if (oldStep) row.offset *= row.step / oldStep;
      if (this.#reduced.matches || this.#static(row)) row.track.style.removeProperty('transform');
      else {
        row.viewport.scrollLeft = 0;
        const expanded = row.viewport.querySelector('details[open]');
        if (expanded) this.#reveal(expanded.closest('article'));
        else { this.#normalize(row); this.#draw(row); }
      }
    }
    this.#sync();
  };

  #paused() {
    return this.#manualPause || this.#hovered || this.#focused || !!this.#drag ||
      this.#reduced.matches || !!this.querySelector('details[open]');
  }

  #sync = () => {
    const paused = this.#paused();
    const deliberate = this.#manualPause || this.#reduced.matches || !!this.querySelector('details[open]');
    this.dataset.paused = String(paused);
    this.dataset.moving = String(!paused && this.#visible && !document.hidden);
    this.#motion?.setAttribute('aria-label', deliberate ? this.dataset.playLabel : this.dataset.pauseLabel);
    this.#motion?.setAttribute('aria-pressed', String(deliberate));
    const run = !paused && this.#visible && !document.hidden && this.#rows.some((row) => !this.#static(row) && row.cards.length > 1);
    if (!run) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      this.#lastTime = 0;
    } else if (!this.#frame) this.#frame = requestAnimationFrame(this.#tick);
  };

  #tick = (time) => {
    this.#frame = 0;
    if (this.#paused() || !this.#visible || document.hidden) return;
    const delta = this.#lastTime ? Math.min(time - this.#lastTime, 50) / 1000 : 0;
    const speed = Math.max(10, Math.min(40, Number(this.dataset.speed) || 20));
    this.#lastTime = time;
    for (const row of this.#rows) {
      if (this.#static(row) || row.cards.length < 2) continue;
      row.offset += delta * speed;
      this.#normalize(row);
      this.#draw(row);
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #step(row) {
    return row.cards[0].offsetWidth + (parseFloat(getComputedStyle(row.track).gap) || 16);
  }

  #normalize(row) {
    if (this.#static(row) || !row.step || row.cards.length < 2) return;
    while (row.offset >= row.step) {
      row.track.append(row.cards.shift());
      row.cards = [...row.track.children];
      row.offset -= row.step;
      row.step = this.#step(row);
    }
    while (row.offset < 0) {
      row.track.prepend(row.cards.pop());
      row.cards = [...row.track.children];
      row.step = this.#step(row);
      row.offset += row.step;
    }
  }

  #draw(row) {
    if (this.#static(row)) { row.track.style.removeProperty('transform'); return; }
    if (!this.#reduced.matches && row.step) row.track.style.transform = 'translate3d(' + (-row.offset).toFixed(2) + 'px, 0, 0)';
  }

  #reveal(card) {
    const row = this.#rows.find((item) => item.track.contains(card));
    if (!row || !card || this.#static(row)) return;
    if (this.#reduced.matches) {
      row.viewport.scrollLeft = card.offsetLeft - (row.width - card.offsetWidth) / 2;
      return;
    }
    row.offset = card.offsetLeft - (row.width - card.offsetWidth) / 2;
    this.#draw(row);
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.#suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      this.#suppressClick = false;
      return;
    }
    const control = event.target.closest('[data-action]');
    if (control) {
      if (control.dataset.action === 'motion') {
        if (this.#manualPause || this.querySelector('details[open]') || this.#hovered || this.#focused) {
          for (const details of this.querySelectorAll('details[open]')) details.open = false;
          this.#manualPause = this.#hovered = this.#focused = false;
          control.blur();
        } else this.#manualPause = true;
      } else {
        this.#manualPause = true;
        const direction = control.dataset.action === 'next' ? 1 : -1;
        for (const row of this.#rows) {
          if (this.#static(row)) continue;
          if (this.#reduced.matches) row.viewport.scrollBy({ left: direction * row.step, behavior: 'instant' });
          else { row.offset += direction * row.step; this.#normalize(row); this.#draw(row); }
        }
      }
      this.#sync();
      return;
    }
    if (event.target.closest('.mxb-testimonials__card') && !event.target.closest('summary')) {
      this.#manualPause = true;
      this.#sync();
    }
  };

  #handleToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement) || !this.contains(event.target)) return;
    if (event.target.open) {
      for (const other of this.querySelectorAll('details[open]')) if (other !== event.target) other.open = false;
      this.#reveal(event.target.closest('article'));
    } else if (!this.querySelector('details[open]')) {
      this.#manualPause = this.#hovered = this.#focused = false;
      this.#measure();
    }
    this.#sync();
  };

  #focusIn = (event) => {
    this.#focused = event.target instanceof Element && event.target.matches(':focus-visible');
    const card = event.target instanceof Element ? event.target.closest('article') : null;
    if (card && this.#focused) this.#reveal(card);
    this.#sync();
  };

  #focusOut = () => {
    queueMicrotask(() => {
      this.#focused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
      this.#sync();
    });
  };

  #keyDown = (event) => {
    if (event.key !== 'Escape') return;
    const details = this.querySelector('details[open]');
    if (details) { details.open = false; details.querySelector('summary')?.focus(); }
  };

  #pointerDown = (event) => {
    if (event.button !== 0 || this.#reduced.matches) return;
    const row = this.#rows.find((item) => item.viewport === event.currentTarget);
    if (!row || this.#static(row)) return;
    this.#drag = { row, id: event.pointerId, x: event.clientX, y: event.clientY, offset: row.offset, horizontal: false };
    this.#sync();
  };

  #pointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.horizontal && Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
      this.#drag = undefined;
      this.#sync();
      return;
    }
    if (!drag.horizontal && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      drag.horizontal = true;
      drag.row.viewport.setPointerCapture(event.pointerId);
      this.#manualPause = true;
    }
    if (drag.horizontal) { event.preventDefault(); drag.row.offset = drag.offset - dx; this.#draw(drag.row); }
  };

  #pointerEnd = (event) => {
    const drag = this.#drag;
    if (!drag || drag.id !== event.pointerId) return;
    this.#suppressClick = drag.horizontal && event.type === 'pointerup';
    clearTimeout(this.#clickResetTimer);
    this.#clickResetTimer = setTimeout(() => { this.#suppressClick = false; }, 0);
    this.#drag = undefined;
    // Ribbons without a play control resume after a swipe ends.
    if (!this.#motion) this.#manualPause = false;
    if (drag.row.viewport.hasPointerCapture(event.pointerId)) drag.row.viewport.releasePointerCapture(event.pointerId);
    this.#normalize(drag.row);
    this.#draw(drag.row);
    this.#sync();
  };

  #blockSelect = (event) => {
    const card = event.target instanceof Element ? event.target.closest('.mxb-testimonials__card') : null;
    if (!card || !this.contains(card)) return;
    this.#manualPause = true;
    this.#reveal(card);
    this.#sync();
  };
}

if (!customElements.get('mindxbridge-testimonials')) customElements.define('mindxbridge-testimonials', MindxbridgeTestimonials);
